import { Component, Suspense, useEffect, useState, type ReactNode } from 'react';
import { Outlet } from 'react-router-dom';
import LoadingPanel from '@shared/components/LoadingPanel';
import { logError } from '@shared/utils/errorLogging';
import AuthenticationError from './AuthenticationError';

type AuthenticationRuntime = {
  MsalProvider: typeof import('@azure/msal-react').MsalProvider;
  msalInstance: typeof import('./msalInstance').msalInstance;
};
type SetupState =
  | { status: 'loading' }
  | { status: 'ready'; runtime: AuthenticationRuntime }
  | { status: 'failed' };

let setupPromise: Promise<AuthenticationRuntime> | undefined;

function loadAuthentication(): Promise<AuthenticationRuntime> {
  // Cache setup across StrictMode effects and navigation between protected pages.
  setupPromise ??= import('./msalInstance')
    .then(async ({ initializeMsal, msalInstance }) => {
      await initializeMsal();
      const { MsalProvider } = await import('@azure/msal-react');
      return { MsalProvider, msalInstance };
    })
    .catch((error: unknown) => {
      logError(error);
      throw error;
    });
  return setupPromise;
}

class AuthenticationErrorBoundary extends Component<
  { children: ReactNode },
  { failed: boolean }
> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidCatch(error: Error) {
    logError(error);
  }

  render() {
    return this.state.failed ? <AuthenticationError /> : this.props.children;
  }
}

export default function AuthenticationLayout() {
  const [setup, setSetup] = useState<SetupState>({ status: 'loading' });

  useEffect(() => {
    let active = true;
    void loadAuthentication().then(
      (runtime) => {
        if (active) {
          setSetup({ status: 'ready', runtime });
        }
      },
      () => {
        if (active) {
          setSetup({ status: 'failed' });
        }
      },
    );
    return () => {
      active = false;
    };
  }, []);

  if (setup.status === 'failed') {
    return <AuthenticationError />;
  }

  if (setup.status === 'loading') {
    return <LoadingPanel />;
  }

  const { MsalProvider, msalInstance } = setup.runtime;
  return (
    <AuthenticationErrorBoundary>
      <MsalProvider instance={msalInstance}>
        <Suspense fallback={<LoadingPanel />}>
          <Outlet />
        </Suspense>
      </MsalProvider>
    </AuthenticationErrorBoundary>
  );
}
